import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  getDefaultView,
  isValidView,
  getViewsForType,
} from '@/utils/viewHelpers';
import { ViewPaths, ViewConfig } from '@/types/views';
import Header from '@/components/Header/Header';
import { useCallback, useMemo } from 'react';
import { HeaderMenu } from '@/components/Header/HeaderMenu';
import { useIntl } from 'react-intl';
import { SearchFilterParams, SortDirection } from '@/types/filterSearch';
import TransactionList from '@/components/Reports/TransactionList.tsx';
import {
  useGetDepartmentsList,
  useListOrganizations,
} from '@/api/trafikportalen';
import { FilteredListContainer } from '@/components/FilteredListContainer';
import { FilesList } from '@/components/Reports/FilesList.tsx';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { DocumentHead } from '@/components/core/DocumentHead';

export const Route = createFileRoute('/$tenantId/_layout/reports/$view')({
  component: ReportView,
  validateSearch: (search: Record<string, unknown>): SearchFilterParams => {
    return {
      query: search.query as string,
      filter: search.filter as string,
      sort: search.sort as string,
      direction: search.direction as SortDirection,
      dateFrom: search.dateFrom as string,
      dateTo: search.dateTo as string,
      department: search.department as string[],
      orderPurchaseMethod: search.orderPurchaseMethod as string[],
      state: search.state as string[],
    };
  },
  beforeLoad: ({ params: { tenantId, view } }) => {
    if (!isValidView(view, 'reports')) {
      throw redirect({
        to: '/$tenantId/reports/$view',
        params: {
          tenantId,
          view: getDefaultView('reports'),
        },
      });
    }
  },
});

function ReportView() {
  const { tenantId, view } = Route.useParams();
  const selected = view as ViewPaths<'reports'>;
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const onViewChange = useCallback(
    (view?: string) => {
      if (!view) return;
      navigate({
        to: Route.to,
        params: { tenantId, view },
        replace: false,
      });
    },
    [navigate, tenantId],
  );

  const views = getViewsForType('reports') as ViewConfig[];

  const metadataKeys = useMemo(() => {
    switch (selected) {
      case 'transactions':
        return {
          titleKey: 'page.reports.transactions.title',
          descriptionKey: 'page.reports.transactions.description',
        };
      case 'files':
        return {
          titleKey: 'page.reports.files.title',
          descriptionKey: 'page.reports.files.description',
        };
      default:
        return {
          titleKey: 'page.reports.title',
          descriptionKey: 'page.reports.description',
        };
    }
  }, [selected]);

  return (
    <>
      <DocumentHead
        titleKey={metadataKeys.titleKey}
        descriptionKey={metadataKeys.descriptionKey}
      />
      <Header
        title={formatMessage({ defaultMessage: 'Rapporter' })}
        description={formatMessage({
          defaultMessage:
            'Notera att vissa typer av transaktioner kan ta upp till en månad att dyka upp.',
        })}
        menu={
          <HeaderMenu
            options={views}
            selected={selected}
            onViewChange={onViewChange}
          />
        }
      />
      {selected === 'transactions' && <TransactionListView />}
      {selected === 'files' && (
        <FilteredListContainer>
          <FilesList />
        </FilteredListContainer>
      )}
    </>
  );
}

const TransactionListView = () => {
  const { data: departments } = useGetDepartmentsList({
    query: {
      enabled: !checkUserPermissions({ permission: 'tenant:viewer' }),
    },
  });
  const { data: organizations } = useListOrganizations({
    query: {
      enabled: checkUserPermissions({ permission: 'tenant:viewer' }),
    },
  });

  const departmentsMap = useMemo(() => {
    return departments?.reduce(
      (acc, department) => {
        acc[department.id] = department.name;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [departments]);

  const organizationsMap = useMemo(() => {
    return organizations?.reduce(
      (acc, organization) => {
        acc[organization.id] = organization.name;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [organizations]);

  return (
    <FilteredListContainer>
      <TransactionList
        departmentsMap={departmentsMap}
        organizationsMap={organizationsMap}
      />
    </FilteredListContainer>
  );
};
